import { eventBus } from '../utils/EventEmitter.js';

/**
 * Manages audio input and analysis
 */
export class AudioManager {
    constructor() {
        this.audioContext = null;
        this.analyser = null;
        this.gainNode = null;
        this.mediaStream = null;
        this.isActive = false;
        this.animationFrame = null;
        
        // Audio analysis data
        this.frequencyData = null;
        this.timeData = null;
        this.audioData = {
            volume: 0,
            frequency: {
                low: 0,
                mid: 0,
                high: 0
            },
            waveform: null
        };
        
        // Smoothing factors for more stable values
        this.smoothingFactor = 0.8;
        this.smoothedVolume = 0;
        this.smoothedFrequencies = {
            low: 0,
            mid: 0,
            high: 0
        };

        // Debug counters
        this.frameCount = 0;
        this.lastDebugTime = 0;
        this.debugInterval = 1000; // Log every second
    }

    /**
     * Start audio capture and analysis
     */
    async startAudio() {
        try {
            // Create audio context if it doesn't exist
            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }

            // Resume audio context (needed for Chrome)
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            // Request microphone access with specific constraints
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                    channelCount: 1,
                    sampleRate: 44100
                } 
            });
            this.mediaStream = stream;

            // Create audio nodes
            const source = this.audioContext.createMediaStreamSource(stream);
            this.gainNode = this.audioContext.createGain();
            this.analyser = this.audioContext.createAnalyser();
            
            // Configure gain (boost signal)
            this.gainNode.gain.value = 5.0; // Increased gain for better sensitivity
            
            // Configure analyser for better sensitivity
            this.analyser.fftSize = 1024;  // Smaller FFT for more responsive analysis
            this.analyser.smoothingTimeConstant = 0.5;  // Reduced smoothing for faster response
            this.analyser.minDecibels = -80; // Increased minimum to catch quieter sounds
            this.analyser.maxDecibels = -10;
            
            // Create data arrays
            this.frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
            this.timeData = new Uint8Array(this.analyser.frequencyBinCount);
            
            // Connect nodes
            source.connect(this.gainNode);
            this.gainNode.connect(this.analyser);
            
            // Verify audio input immediately
            await this.verifyAudioInput();
            
            // Start analysis loop
            this.isActive = true;
            this.analyzeAudio();
            
            // Emit started event
            eventBus.emit('audio:started');
            
            // Debug log
            console.log('Audio analysis started with:', {
                sampleRate: this.audioContext.sampleRate,
                fftSize: this.analyser.fftSize,
                frequencyBinCount: this.analyser.frequencyBinCount,
                smoothingTimeConstant: this.analyser.smoothingTimeConstant,
                minDecibels: this.analyser.minDecibels,
                maxDecibels: this.analyser.maxDecibels,
                gainValue: this.gainNode.gain.value
            });
            
        } catch (error) {
            console.error('Failed to start audio:', error);
            eventBus.emit('audio:error', error);
            throw error;
        }
    }

    /**
     * Verify we're getting audio input
     */
    async verifyAudioInput() {
        // Wait a short time for the audio system to stabilize
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Get initial data
        const testData = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteTimeDomainData(testData);
        
        // Check if we're getting any signal
        let hasSignal = false;
        let maxDeviation = 0;
        let avgDeviation = 0;
        
        for (let i = 0; i < testData.length; i++) {
            const deviation = Math.abs(testData[i] - 128);
            maxDeviation = Math.max(maxDeviation, deviation);
            avgDeviation += deviation;
            if (deviation > 1) { // More sensitive threshold
                hasSignal = true;
            }
        }
        avgDeviation /= testData.length;
        
        console.log('Audio input verification:', {
            hasSignal,
            maxDeviation,
            avgDeviation,
            audioContextState: this.audioContext.state,
            streamActive: this.mediaStream.active,
            gainValue: this.gainNode.gain.value,
            analyserSettings: {
                fftSize: this.analyser.fftSize,
                minDecibels: this.analyser.minDecibels,
                maxDecibels: this.analyser.maxDecibels,
                smoothingTimeConstant: this.analyser.smoothingTimeConstant
            }
        });
        
        return hasSignal;
    }

    /**
     * Stop audio capture
     */
    stopAudio() {
        if (this.mediaStream) {
            // Stop all tracks
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
            
            // Cancel animation frame
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }
            
            this.isActive = false;
            
            // Reset audio data
            this.audioData = {
                volume: 0,
                frequency: {
                    low: 0,
                    mid: 0,
                    high: 0
                },
                waveform: null
            };
            
            // Reset smoothed values
            this.smoothedVolume = 0;
            this.smoothedFrequencies = {
                low: 0,
                mid: 0,
                high: 0
            };
            
            // Reset debug counters
            this.frameCount = 0;
            this.lastDebugTime = 0;
            
            // Emit stopped event
            eventBus.emit('audio:stopped');
            console.log('Audio analysis stopped');
        }
    }

    /**
     * Smooth a value using exponential moving average
     * @param {number} current - Current value
     * @param {number} previous - Previous smoothed value
     * @returns {number} Smoothed value
     */
    smoothValue(current, previous) {
        return this.smoothingFactor * previous + (1 - this.smoothingFactor) * current;
    }

    /**
     * Debug raw audio data
     */
    debugRawData() {
        // Calculate some basic stats about the frequency data
        let maxFreq = 0, avgFreq = 0;
        for (let i = 0; i < this.frequencyData.length; i++) {
            maxFreq = Math.max(maxFreq, this.frequencyData[i]);
            avgFreq += this.frequencyData[i];
        }
        avgFreq /= this.frequencyData.length;

        // Calculate time domain stats
        let maxTime = 0, avgTime = 0;
        for (let i = 0; i < this.timeData.length; i++) {
            maxTime = Math.max(maxTime, Math.abs(this.timeData[i] - 128));
            avgTime += Math.abs(this.timeData[i] - 128);
        }
        avgTime /= this.timeData.length;

        console.log('Raw Audio Data Stats:', {
            frequency: {
                max: maxFreq,
                average: avgFreq,
                nonZeroBins: this.frequencyData.filter(v => v > 0).length
            },
            timeDomain: {
                maxAmplitude: maxTime,
                averageAmplitude: avgTime
            }
        });
    }

    /**
     * Analyze audio data
     */
    analyzeAudio = () => {
        if (!this.isActive) return;

        this.frameCount++;
        const now = performance.now();

        // Get frequency and time domain data
        this.analyser.getByteFrequencyData(this.frequencyData);
        this.analyser.getByteTimeDomainData(this.timeData);

        // Enhanced signal detection with lower threshold
        let signalStrength = 0;
        let maxAmplitude = 0;
        for (let i = 0; i < this.timeData.length; i++) {
            const amplitude = Math.abs(this.timeData[i] - 128);
            maxAmplitude = Math.max(maxAmplitude, amplitude);
            signalStrength += amplitude;
        }
        signalStrength /= this.timeData.length;

        const hasSignal = maxAmplitude > 1 || signalStrength > 0.5; // Lower thresholds

        // Calculate volume (RMS) with improved sensitivity
        let sum = 0;
        for (let i = 0; i < this.timeData.length; i++) {
            const amplitude = (this.timeData[i] - 128) / 128;
            sum += amplitude * amplitude;
        }
        const instantVolume = Math.sqrt(sum / this.timeData.length) * 8; // Increased sensitivity multiplier
        this.smoothedVolume = this.smoothValue(instantVolume, this.smoothedVolume);
        this.audioData.volume = Math.min(1, this.smoothedVolume);

        // Analyze frequency bands with improved sensitivity
        const lowEnd = Math.floor(this.frequencyData.length * 0.1);
        const midEnd = Math.floor(this.frequencyData.length * 0.4);

        let [lowSum, midSum, highSum] = [0, 0, 0];
        let [maxLow, maxMid, maxHigh] = [0, 0, 0];
        let [lowCount, midCount, highCount] = [0, 0, 0];

        // Count non-zero values in each band
        for (let i = 0; i < this.frequencyData.length; i++) {
            const value = this.frequencyData[i];
            if (value > 0) {
                if (i < lowEnd) {
                    lowCount++;
                    lowSum += value;
                    maxLow = Math.max(maxLow, value);
                } else if (i < midEnd) {
                    midCount++;
                    midSum += value;
                    maxMid = Math.max(maxMid, value);
                } else {
                    highCount++;
                    highSum += value;
                    maxHigh = Math.max(maxHigh, value);
                }
            }
        }

        // Calculate normalized values with improved sensitivity
        const instantLow = lowCount > 0 ? (lowSum / (lowCount * 255) + maxLow / 255) / 2 * 3 : 0;
        const instantMid = midCount > 0 ? (midSum / (midCount * 255) + maxMid / 255) / 2 * 3 : 0;
        const instantHigh = highCount > 0 ? (highSum / (highCount * 255) + maxHigh / 255) / 2 * 3 : 0;

        // Smooth and store frequency values
        this.smoothedFrequencies.low = Math.min(1, this.smoothValue(instantLow, this.smoothedFrequencies.low));
        this.smoothedFrequencies.mid = Math.min(1, this.smoothValue(instantMid, this.smoothedFrequencies.mid));
        this.smoothedFrequencies.high = Math.min(1, this.smoothValue(instantHigh, this.smoothedFrequencies.high));

        // Update audio data
        this.audioData.frequency.low = this.smoothedFrequencies.low;
        this.audioData.frequency.mid = this.smoothedFrequencies.mid;
        this.audioData.frequency.high = this.smoothedFrequencies.high;

        // Store normalized waveform data
        this.audioData.waveform = Array.from(this.timeData).map(v => (v - 128) / 128);

        // Debug log with enhanced information
        if (now - this.lastDebugTime > this.debugInterval) {
            console.log('Audio Analysis Details:', {
                frameCount: this.frameCount,
                signalDetection: {
                    maxAmplitude,
                    signalStrength,
                    hasSignal
                },
                volume: {
                    instant: instantVolume,
                    smoothed: this.audioData.volume
                },
                frequencies: {
                    counts: { low: lowCount, mid: midCount, high: highCount },
                    peaks: { low: maxLow / 255, mid: maxMid / 255, high: maxHigh / 255 },
                    instant: { low: instantLow, mid: instantMid, high: instantHigh },
                    smoothed: this.audioData.frequency
                }
            });
            this.lastDebugTime = now;
        }

        // Emit audio data event only if we have signal
        if (hasSignal) {
            eventBus.emit('audio:data', this.audioData);
        }

        // Request next frame
        this.animationFrame = requestAnimationFrame(this.analyzeAudio);
    }

    /**
     * Get current audio data
     * @returns {Object} Current audio analysis data
     */
    getAudioData() {
        return this.audioData;
    }

    /**
     * Check if audio is active
     * @returns {boolean}
     */
    isAudioActive() {
        return this.isActive;
    }
} 